import { useContext, useState } from 'react'
import { Button } from 'app/components/ui/button'
import type { UseOpsPipelineReconcileReturn } from 'app/types/system-pipeline-reconcile'
import { PAGE_GUTTER, PageGutterContext } from 'app/components/layout/PageScroll'
import { cn } from 'app/lib/utils'

export function OpsPipelineReconcileComponent({
  workspaceName,
  scanning,
  applying,
  results,
  error,
  applied,
  scan,
  applyFixes,
}: UseOpsPipelineReconcileReturn): React.ReactElement {
  const gutter = useContext(PageGutterContext)
  // Naming the workspace on a second click is the only guard on the write. Board membership is
  // real patient data and workspace scope is one browser-wide cookie that changes under you, so
  // "add 238" must not be one click away from whichever practice the tab last landed on.
  const [armed, setArmed] = useState(false)
  const totalMissing = (results ?? []).reduce((sum, board) => sum + board.add.length, 0)
  const busy = scanning || applying

  return (
    // Inside the Ops well, main's gutter is gone, so this page takes the well's inset and top.
    <div className={cn(`flex flex-col gap-6`, gutter === `inset` ? cn(PAGE_GUTTER.inset, `pb-6 pt-8`) : `p-6`)}>
      <div className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold">Pipeline reconcile</h1>
        <p className="text-sm text-subtle">
          Compares each system board&rsquo;s queue against who actually holds a membership, for{` `}
          <span className="font-medium">{workspaceName ?? `the current workspace`}</span>. Missing memberships can be
          added here. Stale ones are listed for review and never removed: a card the queue no longer admits is often a
          workflow having worked, such as one moved to Scheduled on booking.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => {
            setArmed(false)
            scan()
          }}
          disabled={busy}
        >
          {scanning ? `Scanning…` : `Scan`}
        </Button>
        {!!totalMissing && (
          <Button
            variant={armed ? `danger` : `secondary`}
            onClick={() => {
              if (armed) {
                setArmed(false)
                applyFixes()
              } else {
                setArmed(true)
              }
            }}
            disabled={busy}
          >
            {applying
              ? `Adding…`
              : armed
                ? `Confirm: add ${totalMissing} to ${workspaceName ?? `this workspace`}`
                : `Add ${totalMissing} missing`}
          </Button>
        )}
        {applied && (
          <span className="text-sm text-subtle">
            Added {applied.added}
            {applied.failed ? `, ${applied.failed} failed` : ``}
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {results?.map((board) => (
        <div key={board.pipelineId} className="rounded-lg border border-default p-4">
          <h2 className="text-sm font-semibold">
            {board.name} <span className="font-normal text-subtle">({board.pipelineId})</span>
          </h2>
          <p className="mt-1 text-sm text-subtle">
            examined {board.examined} · in sync {board.ok} · missing {board.add.length} · stale {board.stale.length}
          </p>

          {!!board.stale.length && (
            <div className="mt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-subtle">
                Stale memberships · review by hand
              </p>
              <ul className="mt-1 space-y-0.5">
                {board.stale.map((contact) => (
                  <li key={contact.id} className="text-sm text-default">
                    {contact.name ?? `(no name)`}
                    <span className="text-subtle">
                      {` `}· {(contact.pipelines ?? []).find((p) => p?.id === board.pipelineId)?.stage ?? `?`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}

      {results && !results.length && <p className="text-sm text-subtle">No membership-backed boards to check.</p>}
    </div>
  )
}
